import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Close,
  CloudUpload,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import ImageCropDialog from './ImageCropDialog';
import BranchSelect from './BranchSelect';
import { LocationChecks, useCreateAtLocations, createAtEach, partialResultMessage } from './multiLocation/shared';
import { useMyLocations } from '../hooks/useMyLocations';

/**
 * In-app destinations an advertisement can open.
 *
 * Sent as a plain string and matched by the app, which falls back to its own
 * default for anything it does not recognise — so a screen only some whitelabel
 * apps have (wellness, shop) is safe to offer here without breaking the others.
 */
const LINK_TARGETS: { value: string; label: string }[] = [
  { value: 'packs', label: 'Packs & memberships' },
  { value: 'sessions', label: 'Class schedule' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'events', label: 'Events list' },
  { value: 'shop', label: 'Shop' },
  { value: 'myPacks', label: "Member's own packs" },
  { value: 'bookings', label: 'My bookings' },
  { value: 'studioAbout', label: 'About the studio' },
  { value: 'trainers', label: 'Trainers' },
  { value: 'gallery', label: 'Gallery' },
];

interface AdvertisementFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultStudioId?: string;
  /** When true: uses studio-scoped API endpoints, locks the studio field, hides global-feed toggle */
  isStudioMode?: boolean;
  advertisement?: {
    _id: string;
    type: 'advertisement' | 'banner';
    title?: string;
    buttonTitle?: string;
    description?: string;
    footerMessage?: string;
    image: string;
    link?: string;
    studio?: string;
    event?: string;
    session?: string;
    isLive: boolean;
    showInGlobalFeed?: boolean;
    titleColor?: string;
    buttonColor?: string;
    buttonTextColor?: string;
  };
}

const AdvertisementFormDialog: React.FC<AdvertisementFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  defaultStudioId,
  isStudioMode = false,
  advertisement,
}) => {
  const isEditMode = Boolean(advertisement);

  // Under "All locations" a new studio advertisement has no implied home —
  // `defaultStudioId` is only the sign-in branch, and the switcher's __all__
  // is refused for writes — so a studio owner picks the location.
  const { isAllLocations, selectedLocationId } = useMyLocations();
  const [branchForNew, setBranchForNew] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  /** The picked file, held locally until the crop is confirmed. Advertisements
   *  only — a banner uploads straight through. */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    type: 'advertisement' as 'advertisement' | 'banner',
    title: '',
    buttonTitle: '',
    description: '',
    footerMessage: '',
    image: '',
    link: '',
    linkTarget: '',
    studio: defaultStudioId || '',
    event: '',
    session: '',
    isLive: true,
    showInGlobalFeed: false,
    titleColor: '',
    buttonColor: '',
    buttonTextColor: '',
  });

  const [studios, setStudios] = useState<any[]>([]);

  // ── CREATE AT SEVERAL LOCATIONS ──────────────────────────────────────────
  // A brand with 2+ locations this account may create at ticks where the ad
  // goes; each ticked location gets its own copy (the ad's studio link is
  // that location). Studios without such a brand keep the form exactly as it was.
  const atLocs = useCreateAtLocations(isStudioMode ? defaultStudioId : formData.studio, {
    enabled: open && !isEditMode && (isStudioMode || !!formData.studio),
    initial: isStudioMode
      ? (selectedLocationId ? [selectedLocationId] : isAllLocations ? [] : [defaultStudioId || ''])
      : [formData.studio],
  });
  const mustPickBranch = isStudioMode && isAllLocations && !isEditMode && !atLocs.multi;
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const resetForm = () => {
    setBranchForNew('');
    setFormData({
      type: 'advertisement',
      title: '',
      buttonTitle: '',
      description: '',
      footerMessage: '',
      image: '',
      link: '',
      linkTarget: '',
      studio: defaultStudioId || '',
      event: '',
      session: '',
      isLive: true,
      showInGlobalFeed: false,
      titleColor: '',
      buttonColor: '',
      buttonTextColor: '',
    });
  };

  const fetchDropdownData = useCallback(async () => {
    try {
      // Studio mode: no need to fetch all studios — the studio field is locked
      if (!isStudioMode) {
        const studiosRes = await apiService.getStudios({});
        const studiosData = studiosRes?.data || studiosRes?.studios || [];
        setStudios(Array.isArray(studiosData) ? studiosData : []);
      }

      const preloadStudioId = advertisement?.studio || defaultStudioId;
      setEventsLoading(true);
      try {
        if (preloadStudioId) {
          const eventsRes = await apiService.getStudioEvents(preloadStudioId as string);
          const eventsData = eventsRes?.events || eventsRes?.data || [];
          setEvents(Array.isArray(eventsData) ? eventsData : []);
        } else {
          // Load all events so the Event dropdown is usable when creating a new ad
          const eventsRes = await apiService.getEvents({});
          const eventsData = eventsRes?.events || eventsRes?.data || [];
          setEvents(Array.isArray(eventsData) ? eventsData : []);
        }
      } finally {
        setEventsLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to fetch dropdown data:', err);
      setStudios([]);
      setEvents([]);
      setEventsLoading(false);
    }
  }, [advertisement?.studio, defaultStudioId, isStudioMode]);

  const fetchAdvertisementDetails = useCallback(async () => {
    if (!advertisement?._id) {
      console.log('No advertisement ID, skipping fetch');
      return;
    }
    
    try {
      console.log('Fetching full advertisement details for ID:', advertisement._id);
      const response = isStudioMode
        ? await apiService.getStudioAdvertisementById(advertisement._id)
        : await apiService.getAdvertisementById(advertisement._id);
      const ad = response.advertisement;
      console.log('Fetched advertisement details:', JSON.stringify(ad, null, 2));
      
      // Extract IDs - handle both string IDs and nested objects
      const studioId = typeof ad.studio === 'string'
        ? ad.studio
        : (typeof ad.studio === 'object' && ad.studio?._id 
          ? ad.studio._id 
          : '');
      const eventId = typeof ad.event === 'string'
        ? ad.event
        : (typeof ad.event === 'object' && ad.event?._id 
          ? ad.event._id 
          : '');
      const sessionId = typeof ad.session === 'string'
        ? ad.session
        : (typeof ad.session === 'object' && ad.session?._id 
          ? ad.session._id 
          : '');
      
      // Determine if link is custom (not auto-generated from studio/event/session)
      let customLink = ad.link || '';
      if (studioId || eventId || sessionId) {
        // Link is auto-generated, don't show it in the custom link field
        customLink = '';
      }
      
      // Update form data with fetched details
      const updatedFormData = {
        type: (ad.type || 'advertisement') as 'advertisement' | 'banner',
        title: ad.title || '',
        buttonTitle: ad.buttonTitle || '',
        description: ad.description || '',
        footerMessage: ad.footerMessage || '',
        image: ad.image || '',
        link: customLink,
        linkTarget: (ad as any).linkTarget || '',
        studio: studioId,
        event: eventId,
        session: sessionId,
        isLive: ad.isLive !== undefined ? ad.isLive : true,
        showInGlobalFeed: ad.showInGlobalFeed ?? false,
        titleColor: ad.titleColor || '',
        buttonColor: ad.buttonColor || '',
        buttonTextColor: ad.buttonTextColor || '',
      };

      console.log('Updating form data with fetched details:', updatedFormData);
      setFormData(updatedFormData);
    } catch (err) {
      console.error('Failed to fetch advertisement details:', err);
    }
  }, [advertisement, isStudioMode]);

  // Fetch dropdown data when dialog opens
  useEffect(() => {
    if (open) {
      fetchDropdownData();
    } else {
      setStudios([]);
      setEvents([]);
    }
  }, [open, fetchDropdownData]);

  // Pre-fill form data when editing - this runs whenever dialog opens or advertisement changes
  useEffect(() => {
    if (!open) {
      // Reset when dialog closes
      resetForm();
      return;
    }

    if (isEditMode && advertisement) {
      console.log('=== EDIT MODE: Pre-filling form ===');
      console.log('Advertisement data received:', JSON.stringify(advertisement, null, 2));
      
      // Extract IDs - handle both string IDs and nested objects
      const studioId = typeof advertisement.studio === 'string' 
        ? advertisement.studio 
        : (typeof advertisement.studio === 'object' && (advertisement.studio as any)?._id 
          ? (advertisement.studio as any)._id 
          : '');
      const eventId = typeof advertisement.event === 'string' 
        ? advertisement.event 
        : (typeof advertisement.event === 'object' && (advertisement.event as any)?._id 
          ? (advertisement.event as any)._id 
          : '');
      const sessionId = typeof advertisement.session === 'string' 
        ? advertisement.session 
        : (typeof advertisement.session === 'object' && (advertisement.session as any)?._id 
          ? (advertisement.session as any)._id 
          : '');
      
      // Determine if link is custom (not auto-generated from studio/event/session)
      let customLink = advertisement.link || '';
      if (studioId || eventId || sessionId) {
        // Link is auto-generated, don't show it in the custom link field
        customLink = '';
      }
      
      // Set form data immediately with ALL fields from the advertisement
      const formDataToSet = {
        type: (advertisement.type || 'advertisement') as 'advertisement' | 'banner',
        title: advertisement.title || '',
        buttonTitle: advertisement.buttonTitle || '',
        description: advertisement.description || '',
        footerMessage: advertisement.footerMessage || '',
        image: advertisement.image || '',
        link: customLink,
        linkTarget: (advertisement as any).linkTarget || '',
        studio: studioId,
        event: eventId,
        session: sessionId,
        isLive: advertisement.isLive !== undefined ? advertisement.isLive : true,
        showInGlobalFeed: advertisement.showInGlobalFeed ?? false,
        titleColor: advertisement.titleColor || '',
        buttonColor: advertisement.buttonColor || '',
        buttonTextColor: advertisement.buttonTextColor || '',
      };
      
      console.log('Form data being set:', formDataToSet);
      setFormData(formDataToSet);
      
      // Then fetch full details to ensure all fields are populated (in case of nested objects)
      // This will update the form if there are any differences
      fetchAdvertisementDetails();
    } else if (!isEditMode) {
      console.log('=== CREATE MODE: Resetting form ===');
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, advertisement?._id]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear related fields when type changes, but preserve the default studio
    if (field === 'type') {
      setFormData(prev => ({
        ...prev,
        type: value,
        studio: defaultStudioId || prev.studio,
        event: '',
        session: '',
        link: '',
      }));
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      event.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB');
      event.target.value = '';
      return;
    }
    // ── ADVERTISEMENT crops, BANNER does not ────────────────────────────
    // They are two different surfaces with two different shapes:
    //
    //   advertisement → a whitelabel app STRIP at a fixed height (Purple
    //     Pilates 280pt, Poise & Pulse and Laya 180pt). Landscape, and the
    //     apps differ, so framing it once here is what stops each app trimming it
    //     differently.
    //
    //   banner → the SweatSocial app's full-width poster, rendered at
    //     `maxWidth * 1.4`, i.e. PORTRAIT. A 1440 × 1000 landscape crop is the
    //     inverse of that shape and would be cropped a second time by
    //     BoxFit.cover, losing its top and bottom. The live banners are
    //     1080 × 1350 and 1080 × 1582 portrait, uploaded raw.
    //
    // Same rule for super admins and studios alike — it follows the TYPE, not
    // the role.
    if (formData.type === 'advertisement') {
      setError(null);
      setPendingFile(file);
      event.target.value = '';
      return;
    }
    await uploadImageFile(file);
    event.target.value = '';
  };

  /** The cropper hands back a 1440 × 1000 JPEG; that is what gets stored. */
  const handleCropped = async (cropped: File) => {
    setPendingFile(null);
    await uploadImageFile(cropped);
  };

  const uploadImageFile = async (file: File) => {
    try {
      setUploadingImage(true);
      setError(null);
      const response = await apiService.uploadFiles([file], 'advertisements');
      const imageUrl = response.fileLinks?.[0] || '';
      if (!imageUrl) throw new Error('Failed to get image URL');
      setFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleDelete = async () => {
    if (!advertisement?._id) return;
    
    if (!window.confirm('Are you sure you want to delete this advertisement? This action cannot be undone and will permanently remove it from the database.')) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      if (isStudioMode) {
        await apiService.deleteStudioAdvertisement(advertisement._id, advertisement.studio || formData.studio);
      } else {
        await apiService.deleteAdvertisement(advertisement._id);
      }
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete advertisement');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.image) {
        setError('Please upload an image');
        return;
      }

      if (mustPickBranch && !branchForNew) {
        setError('Choose which location this advertisement is for');
        return;
      }
      if (atLocs.multi && !atLocs.selected.length) {
        setError('Choose at least one location for this advertisement');
        return;
      }
      // Studio-mode writes are pinned: the ad's own location on edit, the
      // chosen one on create under "All" (else the switcher's location, as before).
      const pinStudioId = isEditMode
        ? advertisement?.studio || formData.studio
        : mustPickBranch ? branchForNew : undefined;

      const payload: any = {
        // Forced, not merely hidden in the UI. A studio editing an existing
        // banner, or any stale state, must not be able to submit one.
        type: isStudioMode ? 'advertisement' : formData.type,
        linkTarget: formData.linkTarget,
        title: formData.title || undefined,
        buttonTitle: formData.buttonTitle || undefined,
        description: formData.description || undefined,
        footerMessage: formData.footerMessage || undefined,
        image: formData.image,
        link: formData.link || undefined,
        studio: formData.studio || undefined,
        event: formData.event || undefined,
        session: formData.session || undefined,
        isLive: formData.isLive,
        showInGlobalFeed: formData.showInGlobalFeed,
        titleColor: formData.titleColor || undefined,
        buttonColor: formData.buttonColor || undefined,
        buttonTextColor: formData.buttonTextColor || undefined,
      };

      if (!isEditMode && atLocs.multi) {
        // An event or session is one location's; with several ticked no copy
        // may link to another location's.
        const body = atLocs.selected.length > 1 ? { ...payload, event: undefined, session: undefined } : payload;
        const { created, failed } = await createAtEach(atLocs.selected, atLocs.nameOf, (studioId) =>
          isStudioMode
            ? apiService.createStudioAdvertisement({ ...body, studio: studioId }, studioId)
            : apiService.createAdvertisement({ ...body, studio: studioId }),
        );
        if (failed.length) {
          // Only the failed locations stay ticked, so a retry makes no duplicates.
          atLocs.setSelected(failed.map((f) => f.studioId));
          setError(partialResultMessage(created, failed, atLocs.nameOf));
          return;
        }
      } else if (isEditMode && advertisement) {
        if (isStudioMode) {
          await apiService.updateStudioAdvertisement({
            advertisementId: advertisement._id,
            ...payload,
          }, pinStudioId);
        } else {
          await apiService.updateAdvertisement({
            advertisementId: advertisement._id,
            ...payload,
          });
        }
      } else {
        if (isStudioMode) {
          await apiService.createStudioAdvertisement(payload, pinStudioId);
        } else {
          await apiService.createAdvertisement(payload);
        }
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Live preview colours with fallbacks matching the Flutter app
  const previewTitleColor = formData.titleColor || '#ffffff';
  const previewButtonBg = formData.buttonColor || '#FF80AB';
  const previewButtonText = formData.buttonTextColor || '#ffffff';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditMode ? 'Edit Advertisement' : 'Create Advertisement'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box display="flex" height="100%">
          {/* ── LEFT: form ── */}
          <Box flex={1} overflow="auto" p={2.5} display="flex" flexDirection="column" gap={2}>
          {/*
            Type Selection.

            A BANNER is a SweatSocial-app surface: it fills the home feed of the
            marketplace app, which is SweatSocial's own shopfront and not any one
            studio's. A studio owner has no banner slot to fill, so offering the
            option only produces content that never renders anywhere. They get
            Advertisement, which is what their own whitelabel app shows.

            Super admins keep both.
          */}
          {isStudioMode ? (
            <TextField
              fullWidth
              label="Type"
              value="Advertisement"
              disabled
              helperText="Advertisements appear in your studio's app. Banners are a SweatSocial app placement and are managed by SweatSocial."
            />
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Type"
              >
                <MenuItem value="advertisement">Advertisement</MenuItem>
                <MenuItem value="banner">Banner</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Image Upload */}
          <Box>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Image *
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {formData.type === 'banner'
                ? 'Uploaded exactly as supplied — no cropping. The SweatSocial app shows banners full-width and tall, so around 1080 × 1400 px works best. Max 10 MB.'
                : 'Any shape works — after picking a file you position it inside a 1440 × 1000 px frame, and that is exactly what members see. Max 10 MB.'}
            </Typography>
            {formData.image ? (
              <Box position="relative" display="inline-block">
                <Box
                  component="img"
                  src={formData.image}
                  alt="Advertisement"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: `1px solid ${colors.divider}`,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={uploadingImage}
                fullWidth
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                }}
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            )}
          </Box>

          {/* Title */}
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            placeholder="Enter advertisement title"
          />

          {/* Button Title */}
          <TextField
            label="Button Title"
            value={formData.buttonTitle}
            onChange={(e) => handleChange('buttonTitle', e.target.value)}
            fullWidth
            placeholder="e.g., Book Now, Learn More"
          />

          {/* Color overrides — only relevant for banner type */}
          {formData.type === 'banner' && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {[
                { label: 'Title Color', key: 'titleColor', defaultHex: '#ffffff' },
                { label: 'Button Background Color', key: 'buttonColor', defaultHex: '#FF80AB' },
                { label: 'Button Text Color', key: 'buttonTextColor', defaultHex: '#ffffff' },
              ].map(({ label, key, defaultHex }) => (
                <Box key={key}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    {label} <span style={{ opacity: 0.6 }}>(optional — leave empty for default)</span>
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <input
                      type="color"
                      value={(formData as any)[key] || defaultHex}
                      onChange={(e) => handleChange(key, e.target.value)}
                      style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 4, padding: 2 }}
                    />
                    <TextField
                      size="small"
                      value={(formData as any)[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Default (${defaultHex})`}
                      fullWidth
                    />
                    {(formData as any)[key] && (
                      <Button size="small" onClick={() => handleChange(key, '')}>Clear</Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Enter description"
          />

          {/* Footer Message */}
          <TextField
            label="Footer Message"
            value={formData.footerMessage}
            onChange={(e) => handleChange('footerMessage', e.target.value)}
            fullWidth
            placeholder="Optional footer message"
          />

          {/* Link Options - Only one can be selected */}
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Link To (Select Only One)
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {atLocs.multi && isStudioMode ? (
                <LocationChecks
                  locations={atLocs.creatable}
                  selected={atLocs.selected}
                  onChange={atLocs.setSelected}
                  severalHint="Each location gets its own copy of this advertisement, shown in that location's app."
                />
              ) : mustPickBranch ? (
                <BranchSelect
                  value={branchForNew}
                  onChange={async (v: string) => {
                    setBranchForNew(v);
                    setFormData(prev => ({ ...prev, studio: v, event: '', link: '', session: '' }));
                    setEventsLoading(true);
                    try {
                      // Pinned read: under "All" the studio events list would
                      // otherwise be the sign-in branch's.
                      const eventsRes = await apiService.getStudioEvents(v, undefined, v);
                      const eventsData = eventsRes?.events || eventsRes?.data || [];
                      setEvents(Array.isArray(eventsData) ? eventsData : []);
                    } catch {
                      setEvents([]);
                    } finally {
                      setEventsLoading(false);
                    }
                  }}
                  minRole="manager"
                />
              ) : isStudioMode ? (
                // Studio mode: studio is locked to the logged-in studio — show read-only field
                <TextField
                  label="Studio"
                  value="Your Studio (locked)"
                  disabled
                  fullWidth
                  helperText="Advertisements are automatically scoped to your studio"
                />
              ) : (
                <Autocomplete
                  options={studios}
                  getOptionLabel={(s: any) => s.studioName || ''}
                  value={studios.find((s: any) => s._id === formData.studio) || null}
                  onChange={async (_: any, newValue: any) => {
                    if (newValue) {
                      setFormData(prev => ({ ...prev, studio: newValue._id, event: '', link: '', session: '' }));
                      setEventsLoading(true);
                      try {
                        const eventsRes = await apiService.getStudioEvents(newValue._id);
                        const eventsData = eventsRes?.events || eventsRes?.data || [];
                        setEvents(Array.isArray(eventsData) ? eventsData : []);
                      } catch {
                        setEvents([]);
                      } finally {
                        setEventsLoading(false);
                      }
                    } else {
                      setFormData(prev => ({ ...prev, studio: '', event: '', session: '' }));
                      setEventsLoading(true);
                      try {
                        const eventsRes = await apiService.getEvents({});
                        const eventsData = eventsRes?.events || eventsRes?.data || [];
                        setEvents(Array.isArray(eventsData) ? eventsData : []);
                      } catch {
                        setEvents([]);
                      } finally {
                        setEventsLoading(false);
                      }
                    }
                  }}
                  disabled={!!formData.event || !!formData.link}
                  renderInput={(params) => <TextField {...params} label="Studio" />}
                />
              )}
              {/* Super admin: the picked studio's brand has other locations. */}
              {atLocs.multi && !isStudioMode && (
                <LocationChecks
                  locations={atLocs.creatable}
                  selected={atLocs.selected}
                  onChange={(ids) =>
                    atLocs.setSelected(formData.studio && !ids.includes(formData.studio) ? [formData.studio, ...ids] : ids)
                  }
                  severalHint="Each location gets its own copy of this advertisement."
                />
              )}

              {/*
                Where the tap LANDS inside the app.

                Previously an advertisement could only point at an external URL
                or one specific event/session, so each app hard-coded a single
                destination for all of them — Coreline sent every ad to the
                schedule tab. A studio advertising a membership had no way to
                land the member on the packs page.

                Picking a screen here clears the event and custom link, since
                "Link To" is one destination, not three.
              */}
              <FormControl fullWidth>
                <InputLabel>Open in app</InputLabel>
                <Select
                  value={formData.linkTarget}
                  label="Open in app"
                  onChange={(e) => {
                    const value = e.target.value as string;
                    setFormData(prev => ({
                      ...prev,
                      linkTarget: value,
                      ...(value ? { event: '', link: '' } : {}),
                    }));
                  }}
                  disabled={!!formData.event || !!formData.link}
                >
                  <MenuItem value="">None — use the app's default</MenuItem>
                  {LINK_TARGETS.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  The screen this advertisement opens when a member taps it.
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Event</InputLabel>
                <Select
                  value={formData.event}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      // Clear studio and link when event is selected
                      handleChange('event', value);
                      setFormData(prev => ({ ...prev, studio: '', link: '', session: '', linkTarget: '' }));
                    } else {
                      handleChange('event', '');
                    }
                  }}
                  label="Event"
                  disabled={!!formData.studio || !!formData.link}
                >
                  <MenuItem value="">None</MenuItem>
                  {eventsLoading ? (
                    <MenuItem value="" disabled>Loading events...</MenuItem>
                  ) : events.length === 0 ? (
                    <MenuItem value="" disabled>No events found</MenuItem>
                  ) : (
                    events.map((event) => (
                      <MenuItem key={event._id} value={event._id}>
                        {event.eventName}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Custom Link"
                value={formData.link}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    // Clear studio and event when custom link is entered
                    handleChange('link', value);
                    setFormData(prev => ({ ...prev, studio: '', event: '', session: '' }));
                  } else {
                    handleChange('link', '');
                  }
                }}
                fullWidth
                placeholder="e.g., https://example.com or /custom-path"
                helperText="Leave empty if linking to studio/event above"
                disabled={!!formData.studio || !!formData.event}
              />
            </Box>
          </Box>

          {/* Status */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isLive}
                onChange={(e) => handleChange('isLive', e.target.checked)}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>Status</Typography>
                <Chip
                  label={formData.isLive ? 'Live' : 'Inactive'}
                  size="small"
                  sx={{
                    backgroundColor: formData.isLive ? colors.success : colors.greyText3,
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
            }
          />

          {/* Show in Global Feed — hidden for studio mode (studio ads stay within their app) */}
          {!isStudioMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showInGlobalFeed}
                  onChange={(e) => handleChange('showInGlobalFeed', e.target.checked)}
                />
              }
              label={
                <Box display="flex" flexDirection="column">
                  <Typography>Show in Global Feed</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Show this ad in the SweatSocial app even if it's linked to a specific studio
                  </Typography>
                </Box>
              }
            />
          )}
          </Box>{/* end form column */}

          {/* ── RIGHT: live preview (banner type only) ── */}
          {formData.type === 'banner' && (
            <Box
              sx={{
                width: 300,
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: 'divider',
                bgcolor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 2,
                gap: 1.5,
                overflow: 'auto',
              }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={1}>
                LIVE PREVIEW
              </Typography>

              {/* Phone frame */}
              <Box
                sx={{
                  width: 240,
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                  border: '6px solid #1a1a1a',
                  position: 'relative',
                  bgcolor: '#000',
                }}
              >
                {/* Banner area */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    // Two different surfaces, two different shapes — so the
                    // preview follows the TYPE rather than picking one and
                    // being wrong for the other half of the time:
                    //
                    //   banner        → SweatSocial app, BigPosterCarousel,
                    //                   height = maxWidth * 1.4 → 140% PORTRAIT
                    //   advertisement → whitelabel app strip, fixed height
                    //                   (PP 280pt, P&P and Laya 180pt) → 71%
                    //
                    // A single 71% was applied to both, which made every
                    // SweatSocial banner preview a landscape lie.
                    paddingTop: formData.type === 'banner' ? '140%' : '71%',
                    overflow: 'hidden',
                    bgcolor: '#222',
                  }}
                >
                  {/* Background image */}
                  {formData.image && (
                    <Box
                      component="img"
                      src={formData.image}
                      alt="preview"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  {!formData.image && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#666',
                        fontSize: 12,
                      }}
                    >
                      No image
                    </Box>
                  )}

                  {/* Gradient overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to bottom, transparent 45%, rgba(0,0,0,0.45) 75%, rgba(0,0,0,0.75) 100%)',
                    }}
                  />

                  {/* Text + button */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      px: 1.5,
                      pb: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    {(formData.title || 'Your Title') && (
                      <Typography
                        sx={{
                          color: previewTitleColor,
                          fontWeight: 800,
                          fontSize: 16,
                          textAlign: 'center',
                          textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          lineHeight: 1.2,
                        }}
                      >
                        {formData.title || 'Your Title'}
                      </Typography>
                    )}
                    {formData.description && (
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.85)',
                          fontSize: 10,
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {formData.description}
                      </Typography>
                    )}
                    {formData.buttonTitle && (
                      <Box
                        sx={{
                          width: '100%',
                          py: 0.9,
                          px: 1,
                          bgcolor: previewButtonBg,
                          borderRadius: 52,
                          border: '1px solid rgba(255,255,255,0.25)',
                          textAlign: 'center',
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: previewButtonText,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          {formData.buttonTitle}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary" textAlign="center">
                Approximate preview — actual app may vary slightly
              </Typography>
            </Box>
          )}
        </Box>{/* end flex row */}
      </DialogContent>

      <ImageCropDialog
        open={!!pendingFile}
        file={pendingFile}
        onCancel={() => setPendingFile(null)}
        onCropped={handleCropped}
      />

      <DialogActions>
        {isEditMode && (
          <Button
            onClick={handleDelete}
            color="error"
            disabled={loading || deleting}
            sx={{ mr: 'auto' }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        )}
        <Button onClick={onClose} disabled={loading || deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploadingImage || deleting || !formData.image}
          sx={{ backgroundColor: colors.primary }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvertisementFormDialog;

